import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { PlayersServices } from "@/services/bedrock";
import { usePaginatedCombobox } from "@/utils";
import { useCoachReviewFilters, type UseCoachReviewFiltersReturn } from "./useCoachReviewFilters";
import type { PaginatedCombobox } from "@/utils/usePaginatedCombobox";

type PlayerOption = { id: number; label: string };

type CoachReviewsFiltersContextValue = UseCoachReviewFiltersReturn & {
  players: PaginatedCombobox<PlayerOption, false>;
  clearAllFilters: () => void;
  clearAllPopoverFilters: () => void;
};

const CoachReviewsFiltersContext = React.createContext<CoachReviewsFiltersContextValue | null>(null);

export function CoachReviewsFiltersProvider({ children }: { children: React.ReactNode }) {
  const coachFilters = useCoachReviewFilters();
  const { playerId, setPlayerId, clearFilters, clearPopoverFilters } = coachFilters;

  const getPlayers = React.useCallback(async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: `${player.fullName || "No Name"} - ${player.email}`,
      })),
    };
  }, []);

  const { data: urlPlayer } = useQuery({
    queryKey: ["coach-reviews-player", playerId],
    queryFn: async () => {
      const player = await PlayersServices.getPlayer(playerId!);
      return {
        id: player.id,
        label: `${player.fullName || "No Name"} - ${player.email}`,
      };
    },
    enabled: playerId != null,
  });

  const playersBase = usePaginatedCombobox(getPlayers, false, urlPlayer ?? null);

  const players = React.useMemo(
    () => ({
      ...playersBase,
      onValueChange: (value: typeof playersBase.value, details: any) => {
        playersBase.onValueChange(value, details);
        setPlayerId(value?.id != null ? Number(value.id) : null);
      },
      reset: () => {
        playersBase.reset();
        setPlayerId(null);
      },
    }),
    [playersBase, setPlayerId]
  );

  const clearAllFilters = React.useCallback(() => {
    clearFilters();
    players.reset();
  }, [clearFilters, players]);

  const clearAllPopoverFilters = React.useCallback(() => {
    clearPopoverFilters();
    players.reset();
  }, [clearPopoverFilters, players]);

  const value = React.useMemo(
    () => ({
      ...coachFilters,
      players,
      clearAllFilters,
      clearAllPopoverFilters,
    }),
    [coachFilters, players, clearAllFilters, clearAllPopoverFilters]
  );

  return <CoachReviewsFiltersContext.Provider value={value}>{children}</CoachReviewsFiltersContext.Provider>;
}

export function useCoachReviewsFilters() {
  const value = React.useContext(CoachReviewsFiltersContext);
  if (!value) {
    throw new Error("useCoachReviewsFilters must be used within CoachReviewsFiltersProvider");
  }
  return value;
}
